import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Button as AntdButton } from 'antd';
import {
  Button,
  Datepicker,
  Spinner,
  Title,
  TrashIcon,
} from '../../components/shared';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { useWidth } from '../../lib/useWidth';
import { sendSingleRequest } from '../../apis';
import { toMillisecond, toSecond } from '../../lib/common.helpers';
import moment from 'moment';

interface IBackupFile {
  file: string;
  last_modified: string;
}

const BackupFilesPage = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const width = useWidth();

  const [files, setFiles] = useState<IBackupFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionFile, setActionFile] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState(Date.now() - 30 * 86400 * 1000);
  const [dateTo, setDateTo] = useState(Date.now());
  const [minDate, setMinDate] = useState(Date.now() - 365 * 86400 * 1000);

  const fetchFiles = async () => {
    setLoading(true);
    const res = await sendSingleRequest({}, 'GET', 'api/account/backup', true);
    setLoading(false);
    if (res.status) {
      setFiles(res.data?.files ?? []);
      setMinDate(
        toMillisecond(res.data?.min_date ?? Date.now() - 365 * 86400 * 1000),
      );
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(
            lang,
            res?.data?.message ?? 'Failed to load backup files',
          ),
        }),
      );
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleCreate = async () => {
    if (dateFrom > dateTo) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, '_date_cannot_later_than'),
        }),
      );
      return;
    }
    if (dateTo < minDate) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(
              lang,
              '_date_cannot_earlier_than',
              moment(minDate).format('DD/MM/YYYY'),
            ),
        }),
      );
      return;
    }
    if (dateFrom > Date.now()) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, '_date_cannot_future'),
        }),
      );
      return;
    }
    setCreating(true);
    const res = await sendSingleRequest(
      {
        date_from: toSecond(dateFrom),
        date_to: toSecond(dateTo),
      },
      'POST',
      'api/account/backup',
      true,
    );
    setCreating(false);
    if (res.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(
            lang,
            res.data?.message ?? 'Backup created successfully',
          ),
        }),
      );
      fetchFiles();
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(
            lang,
            res?.data?.message ?? 'Failed to create backup',
          ),
        }),
      );
    }
  };

  const handleDownload = async (fileName: string) => {
    setActionFile(fileName);
    try {
      const response = await axios({
        method: 'GET',
        url: `${process.env.REACT_APP_API_URL}api/account/backup/download`,
        params: { file: fileName },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('marine-farm')}`,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, '_failed_download_backup'),
        }),
      );
    }
    setActionFile(null);
  };

  const handleDelete = async (fileName: string) => {
    setActionFile(fileName);
    const res = await sendSingleRequest(
      { file: fileName },
      'DELETE',
      'api/account/backup',
      true,
    );
    setActionFile(null);
    if (res.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(
            lang,
            res.data?.message ?? 'Backup deleted successfully',
          ),
        }),
      );
      setFiles(prev => prev.filter(f => f.file !== fileName));
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(
            lang,
            res?.data?.message ?? 'Failed to delete backup',
          ),
        }),
      );
    }
  };

  return (
    <div className='content pb-32'>
      {width > 768 && (
        <Title
          className='mb-24'
          size={5}
          color='black-3'
          align='default'
          fontWeight={600}
        >
          {translate(lang, 'Backup Files')}
        </Title>
      )}
      <div className='mb-24' style={{ maxWidth: 560 }}>
        <div className='d-flex mb-16' style={{ gap: 12 }}>
          <div className='flex-1'>
            <Datepicker
              label={translate(lang, 'Date from')}
              defaultValue={dateFrom ?? undefined}
              onChange={v => v && setDateFrom(v.toDate().getTime())}
            />
          </div>
          <div className='flex-1'>
            <Datepicker
              label={translate(lang, 'Date to')}
              defaultValue={dateTo ?? undefined}
              onChange={v => v && setDateTo(v.toDate().getTime())}
            />
          </div>
        </div>
        <Button
          type='fill'
          width='small'
          size={2}
          color='blue'
          onClick={handleCreate}
          disabled={creating}
        >
          {creating
            ? translate(lang, 'Creating...')
            : translate(lang, 'Create Backup')}
        </Button>
      </div>
      {loading ? (
        <Spinner />
      ) : files.length === 0 ? (
        <p style={{ color: '#888', fontSize: 14 }}>
          {translate(lang, 'No backup files found.')}
        </p>
      ) : (
        <div className='card' style={{ maxWidth: 780, width: '100%' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid #e8e8e8' }}>
                <th
                  style={{
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                  }}
                >
                  {translate(lang, 'File')}
                </th>
                <th
                  style={{
                    padding: '10px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                  }}
                >
                  {translate(lang, 'Created At')}
                </th>
                <th
                  style={{
                    padding: '10px 16px',
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  {translate(lang, 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {files.map(f => (
                <tr key={f.file} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td
                    style={{
                      padding: '10px 16px',
                      wordBreak: 'break-all',
                      color: '#333',
                    }}
                  >
                    {f.file}
                  </td>
                  <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                    {moment(f.last_modified).fromNow()}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <AntdButton
                        type='default'
                        size='small'
                        onClick={() => handleDownload(f.file)}
                        disabled={actionFile === f.file}
                        style={{
                          fontSize: 11,
                          color: '#27ae60',
                          borderColor: '#27ae60',
                        }}
                      >
                        {translate(lang, 'Download')}
                      </AntdButton>
                      <AntdButton
                        type='text'
                        danger
                        icon={<TrashIcon />}
                        onClick={() => handleDelete(f.file)}
                        disabled={actionFile === f.file}
                        title={translate(lang, 'Delete')}
                        style={{
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BackupFilesPage;
